import { useState, useCallback, useRef, useEffect } from "react";
import {
  CheckCircle2,
  AlertTriangle,
  Clock,
  Send,
  Unplug,
  RefreshCw,
  ArrowRight,
} from "lucide-react";
import { useLocale } from "../../../context/LanguageContext";
import { STATUS_CONFIG, STATUS_LABEL_KEYS, TEST_BROADCAST_COOLDOWN_MS } from "../constants";
import { BroadcastSettings } from "./BroadcastSettings";
import { ChannelSelector } from "./ChannelSelector";
import slackIcon from "../../../../assets/icons/slack-icon-panel.png";
import type {
  ChatIntegration,
  BroadcastChannel,
  SelectBroadcastChannelInput,
  UpdateBroadcastSettingsInput,
} from "../types";

// ─── Helper: format relative time ───────────────────────────────────────────

function formatRelativeTime(dateStr: string, t: (key: string, params?: Record<string, string | number>) => string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return t("integrations.slack.timeJustNow");
  if (minutes < 60) return t("integrations.slack.timeMinutesAgo", { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t("integrations.slack.timeHoursAgo", { count: hours });
  const days = Math.floor(hours / 24);
  return t("integrations.slack.timeDaysAgo", { count: days });
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface SlackIntegrationCardProps {
  integration: ChatIntegration | null;
  channels: BroadcastChannel[];
  channelsLoading: boolean;
  onConnect: () => Promise<string>;
  onDisconnect: () => Promise<void>;
  onSelectChannel: (input: SelectBroadcastChannelInput) => Promise<void>;
  onUpdateSettings: (input: UpdateBroadcastSettingsInput) => Promise<void>;
  onRefreshStatus: () => Promise<void>;
  onTestBroadcast: () => Promise<void>;
  onRefreshChannels?: () => void;
  onPopupClosed?: () => void;
}

export function SlackIntegrationCard({
  integration,
  channels,
  channelsLoading,
  onConnect,
  onDisconnect,
  onSelectChannel,
  onUpdateSettings,
  onRefreshStatus,
  onTestBroadcast,
  onRefreshChannels,
  onPopupClosed,
}: SlackIntegrationCardProps) {
  const { t } = useLocale();
  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testCooldown, setTestCooldown] = useState(false);
  const [showChannelSelector, setShowChannelSelector] = useState(false);
  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const popupRef = useRef<Window | null>(null);

  const showToast = useCallback(
    (type: "success" | "error", message: string) => {
      setToast({ type, message });
      setTimeout(() => setToast(null), 4000);
    },
    [],
  );

  // ── Monitor popup window — refetch when it closes ──────────────────────────

  useEffect(() => {
    if (!popupRef.current) return;
    const interval = setInterval(() => {
      if (popupRef.current?.closed) {
        popupRef.current = null;
        clearInterval(interval);
        console.log("[SlackOAuth] Popup closed — refetching integration status");
        onPopupClosed?.();
      }
    }, 1000);
    return () => clearInterval(interval);
  });

  // ── OAuth Connect ──────────────────────────────────────────────────────────

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const authUrl = await onConnect();
      const tab = window.open(authUrl, "_blank");
      popupRef.current = tab;
      console.log("[SlackOAuth] New tab opened:", { tab: !!tab, url: authUrl });
    } catch {
      showToast("error", t("integrations.slack.connectFailed"));
    } finally {
      setConnecting(false);
    }
  };

  // ── Disconnect ─────────────────────────────────────────────────────────────────────

  const handleDisconnect = async () => {
    setDisconnecting(true);
    try {
      await onDisconnect();
      setShowDisconnectConfirm(false);
      showToast("success", t("integrations.slack.disconnected"));
    } catch {
      showToast("error", t("integrations.slack.disconnectFailed"));
    } finally {
      setDisconnecting(false);
    }
  };

  // ── Refresh Status ─────────────────────────────────────────────────────────

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await onRefreshStatus();
    } catch {
      showToast("error", t("integrations.slack.refreshFailed"));
    } finally {
      setRefreshing(false);
    }
  };

  // ── Test Broadcast ─────────────────────────────────────────────────────────

  const handleTest = async () => {
    setTesting(true);
    try {
      await onTestBroadcast();
      showToast("success", t("integrations.slack.testSuccess"));
      setTestCooldown(true);
      setTimeout(() => setTestCooldown(false), TEST_BROADCAST_COOLDOWN_MS);
    } catch {
      showToast("error", t("integrations.slack.testFailed"));
    } finally {
      setTesting(false);
    }
  };

  // ── Determine display state ────────────────────────────────────────────────

  const status = integration?.status;
  const isConnected = status === "CONNECTED";
  const isPending = status === "PENDING_APPROVAL";
  const isDisconnected = status === "DISCONNECTED";
  const isRevoked = status === "REVOKED" || status === "ERROR" || isDisconnected;
  const hasChannel = isConnected && !!integration?.channelId;
  const isFullyOperational = hasChannel;

  return (
    <div className="rounded-lg border border-border bg-card">
      {/* Card header */}
      <div className="flex items-start justify-between p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
            <img src={slackIcon} alt="Slack" className="h-6 w-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-foreground">Slack</h3>
              {status && (
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_CONFIG[status].bg} ${STATUS_CONFIG[status].color}`}
                >
                  {isConnected && <CheckCircle2 className="h-3 w-3" />}
                  {isPending && <Clock className="h-3 w-3" />}
                  {(status === "REVOKED" || status === "ERROR" || isDisconnected) && <AlertTriangle className="h-3 w-3" />}
                  {t(STATUS_LABEL_KEYS[status])}
                </span>
              )}
            </div>
            {/* Workspace & channel info */}
            {integration?.workspaceName && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                {t("integrations.slack.workspace", { name: integration.workspaceName })}
              </p>
            )}
            {integration?.channelName && isConnected && (
              <div className="mt-1 inline-flex items-center gap-1 rounded-md border border-border bg-muted/50 px-2 py-0.5">
                <span className="text-xs font-medium text-foreground">#{integration.channelName}</span>
              </div>
            )}
            {isFullyOperational && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                {integration.lastBroadcastAt
                  ? t("integrations.slack.lastBroadcast", { time: formatRelativeTime(integration.lastBroadcastAt, t) })
                  : t("integrations.slack.noBroadcasts")}
                {" · "}
                {t("integrations.slack.totalBroadcasts", { count: integration.totalBroadcasts })}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Inline toast */}
      {toast && (
        <div
          className={`mx-4 mb-3 flex items-center gap-2 rounded-md border px-3 py-2 text-xs ${
            toast.type === "success"
              ? "border-success/30 bg-success/10 text-success"
              : "border-destructive/30 bg-destructive/10 text-destructive"
          }`}
        >
          {toast.type === "success" ? (
            <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
          )}
          {toast.message}
        </div>
      )}

      {/* ── State 1: Not Connected ──────────────────────────────────────────── */}
      {!integration && (
        <div className="border-t border-border px-4 py-4">
          <p className="text-sm text-muted-foreground">
            {t("integrations.slack.connectDescription")}
          </p>
          <button
            type="button"
            onClick={handleConnect}
            disabled={connecting}
            className="mt-3 inline-flex h-8 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {connecting ? (
              <>
                <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                {t("integrations.slack.connecting")}
              </>
            ) : (
              <>
                {t("integrations.slack.connect")} <ArrowRight className="h-3.5 w-3.5" />
              </>
            )}
          </button>
        </div>
      )}

      {/* ── State 2: Pending Approval ─────────────────────────────────────────────── */}
      {isPending && (
        <div className="border-t border-border px-4 py-4">
          <p className="text-sm text-muted-foreground">
            {t("integrations.slack.pendingDescription")}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={handleRefresh}
              disabled={refreshing}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              {t("integrations.slack.refreshStatus")}
            </button>
            <button
              type="button"
              onClick={() => setShowDisconnectConfirm(true)}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-destructive/30 px-3 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
            >
              <Unplug className="h-3.5 w-3.5" />
              {t("integrations.slack.disconnect")}
            </button>
          </div>
        </div>
      )}

      {/* ── State 3: Connected — No Channel Selected ────────────────────────── */}
      {isConnected && !hasChannel && (
        <div className="border-t border-border px-4 py-4 space-y-3">
          <ChannelSelector
            channels={channels}
            selectedChannelId={null}
            loading={channelsLoading}
            onSelect={onSelectChannel}
            onRefresh={onRefreshChannels}
          />
          <button
            type="button"
            onClick={() => setShowDisconnectConfirm(true)}
            className="inline-flex h-8 items-center gap-1.5 rounded-md border border-destructive/30 px-3 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
          >
            <Unplug className="h-3.5 w-3.5" />
            {t("integrations.slack.disconnect")}
          </button>
        </div>
      )}

      {/* ── State 4: Fully Operational ──────────────────────────────────────── */}
      {isFullyOperational && integration && (
        <div className="border-t border-border px-4 py-4 space-y-4">
          {/* Broadcast settings toggles */}
          <BroadcastSettings
            integration={integration}
            onUpdate={onUpdateSettings}
          />

          {/* Channel change (collapsible) */}
          {showChannelSelector && (
            <ChannelSelector
              channels={channels}
              selectedChannelId={integration.channelId}
              loading={channelsLoading}
              onSelect={async (input) => {
                await onSelectChannel(input);
                setShowChannelSelector(false);
              }}
              onRefresh={onRefreshChannels}
            />
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleTest}
              disabled={testing || testCooldown}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Send className={`h-3.5 w-3.5 ${testing ? "animate-pulse" : ""}`} />
              {testing
                ? t("integrations.slack.sending")
                : testCooldown
                  ? t("integrations.slack.testSentCooldown")
                  : t("integrations.slack.sendTestMessage")}
            </button>
            <button
              type="button"
              onClick={() => setShowChannelSelector(!showChannelSelector)}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              {t("integrations.slack.changeChannel")}
            </button>
            <button
              type="button"
              onClick={() => setShowDisconnectConfirm(true)}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-destructive/30 px-3 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
            >
              <Unplug className="h-3.5 w-3.5" />
              {t("integrations.slack.disconnect")}
            </button>
          </div>
        </div>
      )}

      {/* ── State 5: Disconnected / Revoked / Error ─────────────────────── */}
      {isRevoked && (
        <div className="border-t border-border px-4 py-4">
          <p className="text-sm text-muted-foreground">
            {isDisconnected
              ? t("integrations.slack.disconnectedMessage")
              : t("integrations.slack.revokedMessage")}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={handleConnect}
              disabled={connecting}
              className="inline-flex h-8 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {connecting ? (
                <>
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-primary-foreground border-t-transparent" />
                  {t("integrations.slack.reconnecting")}
                </>
              ) : (
                t("integrations.slack.reconnect")
              )}
            </button>
            <button
              type="button"
              onClick={() => setShowDisconnectConfirm(true)}
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              {t("integrations.slack.remove")}
            </button>
          </div>
        </div>
      )}

      {/* ── Disconnect Confirmation Dialog (inline) ───────────────────────────── */}
      {showDisconnectConfirm && (
        <div className="border-t border-border bg-destructive/5 px-4 py-3">
          <p className="text-sm font-medium text-foreground">
            {t("integrations.slack.disconnectConfirmTitle")}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("integrations.slack.disconnectConfirmMessage")}
          </p>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="inline-flex h-8 items-center gap-1.5 rounded-md bg-destructive px-4 text-sm font-medium text-destructive-foreground transition-colors hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {disconnecting ? (
                <>
                  <span className="h-3 w-3 animate-spin rounded-full border-2 border-destructive-foreground border-t-transparent" />
                  {t("integrations.slack.disconnecting")}
                </>
              ) : (
                t("integrations.slack.confirmDisconnect")
              )}
            </button>
            <button
              type="button"
              onClick={() => setShowDisconnectConfirm(false)}
              disabled={disconnecting}
              className="inline-flex h-8 items-center rounded-md border border-border px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
